import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetCommodityConditions } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import CommodityConditionHttpService from '../../../services/http/masterData/commodityConditionHttpService';
import CommodityConditionForm from './form/CommodityConditionForm';
import { setCommodityConditionMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { useLocation } from 'react-router-dom';
import { ICommodityCondition } from '../../../@types/model/masterData/commodityCondition/commodityCondition';
import { ICommodityConditionFormValues } from '../../../@types/model/masterData/commodityCondition/commodityConditionFormValues';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import UserHelper from '../../../services/helper/userHelper';
import CommodityConditionModelHelper from '../../../@types/model/masterData/commodityCondition/commodityConditionModelHelper';

const CommodityConditionScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const commodityConditions = useAppSelector(x => x.masterData.commodityConditions);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedCommodityCondition, setSelectedCommodityCondition] = useState<ICommodityCondition | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingCommodityCondition, setDeletingCommodityCondition] = useState<ICommodityCondition | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await CommodityConditionHttpService.getCommodityConditionData();
                    dispatch(dataSetCommodityConditions(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError | undefined;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading commodity conditions.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async (values : ICommodityConditionFormValues) => {
        setIsLoading(true);

        try {
            const upsert = CommodityConditionModelHelper.createUpsert(values);
            const res = await CommodityConditionHttpService.addOrUpdateCommodityCondition(upsert);
            const updatedList = upsertArrayElement(commodityConditions, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCommodityConditions(updatedList));
            await setCommodityConditionMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedCommodityCondition ? 'Commodity Condition updated successfully.' : 'Commodity Condition added successfully.');
            onModalDismiss();
        } catch (e) {
            const apiError = e as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating Commodity Condition data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeCommodityCondition = async () => {
        if (!deletingCommodityCondition) return;
        const updatedCommodityCondition = { ...deletingCommodityCondition, isActive: false };
        setIsLoading(true);

        try {
            const res = await CommodityConditionHttpService.deleteCommodityCondition(updatedCommodityCondition.id);
            const updatedList = upsertArrayElement(commodityConditions, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCommodityConditions(updatedList));
            await setCommodityConditionMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Commodity Condition updated successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting Commodity Condition.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedCommodityCondition(null);
    };

    const edit = (selectedRow : ICommodityCondition) => {
        setSelectedCommodityCondition(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ICommodityCondition) : void => {
        setDeletingCommodityCondition(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingCommodityCondition(null);
    };

    const deleteButtonTooltip = (row : ICommodityCondition) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Commodity Condition', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatCommodities = useCallback((commodityIds : Array<number>) => {
        return commodities.filter(x => commodityIds.includes(x.id))
            .map(x => x.name).join(', ');
    }, [commodities]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return CommodityConditionModelHelper.createFormValues(selectedCommodityCondition, commodities);
        }
    }, [selectedCommodityCondition, isDialogOpen, commodities]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<ICommodityCondition>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Commodity Condition', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Commodity Condition', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : ICommodityCondition) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}
    
                        enableSorting
                        fitWidthToPage
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Condition', field: 'condition', enableFiltering: true, enableSorting: true },
                            { title: 'Commodities', field: 'commodityIds', formatFunction: formatCommodities, width: 200, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={commodityConditions}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedCommodityCondition ? 'Edit Commodity Condition' : 'Add Commodity Condition'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <CommodityConditionForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                        isMasterView={true}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Commodity Condition'}
                open={!!deletingCommodityCondition}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeCommodityCondition}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default CommodityConditionScreen;
